import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const CustomerStories = () => {
  const [selectedStory, setSelectedStory] = useState(0);

  const customerStories = [
    {
      id: 1,
      name: "The Rodriguez Family",
      occasion: "25th Wedding Anniversary",
      story: `When Maria and Carlos Rodriguez were planning their 25th wedding anniversary celebration, they knew they wanted something special that would bring their extended family together. Having discovered Kebab Palace through a friend's recommendation, they decided to host their celebration in our private dining area.What made this evening truly memorable wasn't just the exceptional food, but how our team went above and beyond to make their celebration perfect. Chef Hassan personally prepared a special mixed grill platter featuring all their favorite dishes, while our staff decorated the space with traditional Mediterranean touches.
      
      The evening became a beautiful blend of cultures as the Rodriguez family, with their Mexican heritage, celebrated alongside the warm Mediterranean hospitality of Kebab Palace. The children played together while adults shared stories over perfectly grilled kebabs and fresh pita bread.
      
      "It felt like we were celebrating with family," Maria shared. "The warmth and care from everyone at Kebab Palace made our anniversary unforgettable. Now it's become our annual tradition."`,
      image: "https://images.unsplash.com/photo-1511795409834-ef04bbd61622?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      testimonial: "Kebab Palace didn\'t just serve us dinner; they created a memory that our family will treasure forever.",
      date: "September 2023",
      dishes: ["Mixed Grill Platter", "Lamb Kebab", "Chicken Shawarma", "Fresh Pita & Hummus"],
      celebration: "25th Wedding Anniversary"
    },
    {
      id: 2,
      name: "The Johnson Graduate Celebration",
      occasion: "Medical School Graduation",
      story: `When Sarah Johnson graduated from medical school, her parents wanted to celebrate this milestone achievement with a dinner that would be as memorable as the accomplishment itself. Having heard about Kebab Palace's reputation for exceptional service and authentic cuisine, they chose our restaurant for this special family gathering.
      
      The celebration brought together three generations of the Johnson family, including Sarah's grandparents who had never experienced Mediterranean cuisine before. Our team took special care to explain each dish and accommodate dietary restrictions, ensuring everyone could participate in the feast.
      
      Chef Omar personally visited their table to congratulate Sarah and share the story behind each dish they were enjoying. The evening featured our signature lamb kebabs, fresh salads, and traditional desserts that sparked conversations about different cultures and culinary traditions.
      
      What started as a graduation dinner became a cultural exchange that opened the family's palate to new flavors and experiences. Sarah's grandmother, initially hesitant about trying new foods, ended up asking for the recipe for our rose water baklava.`,
      image: "https://images.unsplash.com/photo-1543007630-9710e4a00a20?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      testimonial: "This dinner marked not just my graduation, but the beginning of our family\'s love affair with Mediterranean cuisine.",
      date: "May 2023",
      dishes: ["Signature Lamb Kebab", "Mediterranean Salad", "Rose Water Baklava", "Turkish Coffee"],
      celebration: "Medical School Graduation"
    },
    {
      id: 3,
      name: "The Chen Family Reunion",
      occasion: "Annual Family Reunion",
      story: `For five consecutive years, the Chen family has chosen Kebab Palace as the venue for their annual family reunion. What started as a convenient location has evolved into a cherished tradition that brings together family members from across the country.
      
      The Chen family reunion is no ordinary gathering – it involves over 30 family members ranging from toddlers to great-grandparents. Our team has learned to anticipate their needs, preparing a special family-style menu that accommodates various dietary preferences and age groups.
      
      Each year, the children look forward to trying new dishes and learning about Mediterranean culture, while the adults appreciate the quality of ingredients and authentic preparation methods. The spacious layout of our restaurant allows different generations to mingle comfortably.
      
      "Kebab Palace has become part of our family story," shares David Chen, the family patriarch. "The staff remembers our preferences, the children have grown up eating here, and we've created countless memories around these tables. It's more than a restaurant to us – it's where our family comes together."`,
      image: "https://images.unsplash.com/photo-1544148103-0773bf10d330?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      testimonial: "Five years running, and Kebab Palace continues to be the heart of our family reunions.",
      date: "August 2019-2023",
      dishes: ["Family Mixed Grill", "Kids' Chicken Tenders", "Vegetarian Platter", "Assorted Desserts"],
      celebration: "Annual Family Reunion"
    },
    {
      id: 4,
      name: "The Thompson First Date",
      occasion: "First Date & Engagement",
      story: `Sometimes the most beautiful love stories begin with a simple dinner. When Michael Thompson was planning his first date with Jennifer, he chose Kebab Palace based on online reviews and the warm atmosphere he experienced during a previous visit with friends.
      
      That first date in 2019 was magical – the intimate lighting, the aromatic spices, and the exceptional service created the perfect backdrop for two people getting to know each other. They shared a mixed appetizer platter, tried each other's dishes, and talked until the restaurant was nearly empty.
      
      Over the next three years, Kebab Palace became "their place." They celebrated birthdays, anniversaries, and career milestones at the same corner table. When Michael decided to propose, there was no question where it would happen.
      
      In December 2022, with the help of our staff, Michael arranged a surprise proposal right at their favorite table. The entire restaurant erupted in applause when Jennifer said yes, and Chef Hassan personally brought out a complimentary dessert to celebrate the engagement.
      
      "Kebab Palace isn't just where we had our first date," Jennifer explains. "It's where our love story unfolded, one meal at a time."`,image: "https://images.unsplash.com/photo-1516997121675-4c2d1684aa3e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",testimonial: "From our first date to our engagement, Kebab Palace has been the backdrop of our love story.",date: "2019-2022",
      dishes: ["Mixed Appetizer Platter", "Grilled Salmon", "Lamb Chops", "Chocolate Baklava"],
      celebration: "First Date & Engagement"
    }
  ];

  const communityImpact = [
    {
      title: "Local School Fundraisers",
      description: "We\'ve donated over $15,000 to local school programs through fundraising dinners and silent auctions.",
      icon: "GraduationCap",
      impact: "$15,000+ donated"
    },
    {
      title: "Community Food Drives",
      description: "During holidays, we prepare and donate meals to local food banks and homeless shelters.",
      icon: "Heart",
      impact: "500+ meals donated"
    },
    {
      title: "Cultural Education Programs",
      description: "We host cooking classes and cultural events to share Mediterranean traditions with our community.",
      icon: "BookOpen",
      impact: "12 events annually"
    },
    {
      title: "Local Business Partnerships",
      description: "We source ingredients from local farms and partner with nearby businesses for community events.",
      icon: "Handshake",
      impact: "25+ local partnerships"
    }
  ];

  return (
    <div className="py-8">
      <div className="text-center mb-16">
        <h2 className="font-headline text-4xl md:text-5xl font-bold text-primary mb-6">
          Community Stories & Connections
        </h2>
        <p className="font-body text-lg text-text-secondary max-w-3xl mx-auto">
          Every meal tells a story, and every celebration creates lasting memories. These are just a few of the countless special moments our community has shared with us over the years.
        </p>
      </div>

      {/* Story Navigation */}
      <div className="flex flex-wrap justify-center gap-4 mb-12">
        {customerStories.map((story, index) => (
          <button
            key={story.id}
            onClick={() => setSelectedStory(index)}
            className={`px-4 py-2 rounded-lg font-cta font-medium transition-organic ${
              selectedStory === index
                ? 'bg-primary text-white shadow-warm'
                : 'text-text-primary hover:text-primary hover:bg-primary-50 border border-primary-200'
            }`}
          >
            {story.name}
          </button>
        ))}
      </div>

      {/* Selected Story */}
      <motion.div
        key={selectedStory}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-16"
      >
        <div className="card-surface">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center">
                  <Icon name="Users" size={24} className="text-white" />
                </div>
                <div>
                  <h3 className="font-headline text-2xl font-bold text-text-primary">
                    {customerStories[selectedStory].name}
                  </h3>
                  <p className="text-accent-600 font-cta font-semibold">
                    {customerStories[selectedStory].occasion}
                  </p>
                  <p className="text-text-secondary text-sm">
                    {customerStories[selectedStory].date}
                  </p>
                </div>
              </div>

              <div className="prose prose-lg max-w-none text-text-secondary mb-6">
                <p>{customerStories[selectedStory].story}</p>
              </div>

              <div className="bg-accent-50 rounded-lg p-4 mb-6">
                <Icon name="Quote" size={24} className="text-accent-600 mb-3" />
                <blockquote className="font-accent text-lg italic text-accent-800 mb-3">
                  "{customerStories[selectedStory].testimonial}"
                </blockquote>
                <cite className="font-body text-sm text-accent-600">
                  — {customerStories[selectedStory].name}
                </cite>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-cta font-semibold text-text-primary mb-2 flex items-center space-x-2">
                    <Icon name="ChefHat" size={16} />
                    <span>Featured Dishes</span>
                  </h4>
                  <ul className="space-y-1">
                    {customerStories[selectedStory].dishes.map((dish, idx) => (
                      <li key={idx} className="text-sm text-text-secondary flex items-center space-x-2">
                        <Icon name="Dot" size={12} className="text-primary" />
                        <span>{dish}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="font-cta font-semibold text-text-primary mb-2 flex items-center space-x-2">
                    <Icon name="Calendar" size={16} />
                    <span>Celebration</span>
                  </h4>
                  <p className="text-sm text-text-secondary">
                    {customerStories[selectedStory].celebration}
                  </p>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="relative overflow-hidden rounded-lg shadow-warm-lg">
                <Image
                  src={customerStories[selectedStory].image}
                  alt={customerStories[selectedStory].name}
                  className="w-full h-96 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary-900/60 to-transparent"></div>
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="bg-white/90 backdrop-blur-sm rounded-lg p-4">
                    <h4 className="font-cta font-semibold text-text-primary mb-1">
                      {customerStories[selectedStory].occasion}
                    </h4>
                    <p className="text-sm text-text-secondary">
                      {customerStories[selectedStory].date}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Community Impact */}
      <div>
        <h3 className="font-headline text-3xl font-bold text-text-primary text-center mb-12">
          Our Community Impact
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {communityImpact.map((impact, index) => (
            <motion.div
              key={impact.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="card-surface hover:shadow-warm-lg transition-organic"
            >
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-trust rounded-full flex items-center justify-center flex-shrink-0">
                  <Icon name={impact.icon} size={24} className="text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-cta text-lg font-semibold text-text-primary">
                      {impact.title}
                    </h4>
                    <span className="text-sm font-semibold text-trust bg-trust-50 px-2 py-1 rounded">
                      {impact.impact}
                    </span>
                  </div>
                  <p className="font-body text-text-secondary leading-relaxed">
                    {impact.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Community Quote */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <div className="max-w-4xl mx-auto card-surface bg-gradient-to-r from-trust-50 to-primary-50">
            <Icon name="Heart" size={48} className="text-trust mx-auto mb-6" />
            <blockquote className="font-accent text-2xl md:text-3xl text-text-primary mb-6 italic">
              "A restaurant is only as strong as the community it serves. We're honored to be part of so many special moments and to give back to the neighborhood that has embraced us as family."
            </blockquote>
            <cite className="font-body text-lg text-text-secondary">
              — Omar Mahmoud, Executive Chef
            </cite>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CustomerStories;